import { useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';

// Field sizing matches the app's canonical inputs (EditProfileModal et al.):
// px-3.5 py-2.5 rounded-xl, text-sm.
export const FIELD_CLASSES = 'w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-zinc-600 bg-white dark:bg-zinc-900 text-sm text-slate-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent';

/**
 * Styled replacement for native <select>: input-height trigger + a themed
 * popover with teal-highlighted selection and (for long lists) a filter box.
 */
export function StyledSelect({ value, onChange, options, placeholder = 'Select…', disabled = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selected = options.find((o) => o.value === value);
  const searchable = options.length > 10;
  const q = query.trim().toLowerCase();
  const filtered = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  const MAX_VISIBLE = 150;
  const visible = filtered.slice(0, MAX_VISIBLE);

  const close = () => { setOpen(false); setQuery(''); };

  return (
    <div className={`relative ${disabled ? 'opacity-60 pointer-events-none' : ''}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`${FIELD_CLASSES} text-left flex items-center justify-between gap-2`}
      >
        <span className={`truncate ${selected && selected.value !== '' ? '' : 'text-slate-400 dark:text-zinc-500'}`}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown size={14} className={`flex-shrink-0 text-slate-400 dark:text-zinc-500 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={close} />
          <div className="absolute z-20 mt-1.5 w-full rounded-xl border border-slate-200 dark:border-zinc-600 bg-white dark:bg-zinc-800 shadow-xl overflow-hidden">
            {searchable && (
              <div className="p-1.5 border-b border-slate-100 dark:border-zinc-700">
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Escape') close(); }}
                  placeholder="Type to filter…"
                  className="w-full px-2.5 py-1.5 rounded-lg text-xs bg-slate-50 dark:bg-zinc-900 text-slate-900 dark:text-zinc-100 border border-slate-200 dark:border-zinc-700 focus:outline-none focus:border-teal-500 placeholder-slate-400 dark:placeholder-zinc-500"
                />
              </div>
            )}
            <div role="listbox" className="max-h-52 overflow-y-auto p-1">
              {visible.map((o) => {
                const isSel = o.value === value;
                return (
                  <button
                    key={o.value || '__empty'}
                    type="button"
                    role="option"
                    aria-selected={isSel}
                    onClick={() => { onChange(o.value); close(); }}
                    className={`w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg text-sm text-left transition-colors
                      ${isSel
                        ? 'bg-teal-50 dark:bg-teal-900/25 text-teal-700 dark:text-teal-300 font-medium'
                        : 'text-slate-700 dark:text-zinc-200 hover:bg-slate-50 dark:hover:bg-zinc-700/60'}`}
                  >
                    <span className="truncate">{o.label}</span>
                    {isSel && <Check size={13} className="flex-shrink-0" />}
                  </button>
                );
              })}
              {filtered.length === 0 && (
                <p className="text-xs text-center py-4 text-slate-400 dark:text-zinc-500">No matches</p>
              )}
              {filtered.length > MAX_VISIBLE && (
                <p className="text-[10px] text-center py-1.5 text-slate-400 dark:text-zinc-500">
                  +{filtered.length - MAX_VISIBLE} more — type to narrow
                </p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
